import axios from 'axios';
import {
  ReactNode, createContext, useContext, useEffect, useState,
} from 'react';

import { fetchFinderConfig } from '../api/configService';
import { resolvePreviewLoginUrl } from '../lib/org';
import { hasPreviewAuthAttempt, isPreviewIntent, setPublished } from '../lib/preview';
import { DEFAULT_CONFIG, FinderConfig } from '../types/finderConfig';

interface AppConfigState {
  config: FinderConfig;
  loading: boolean;
  error: Error | null;
  /** Boot /finder/config returned 404 — three-gate failed (unknown/unpublished org). */
  notFound: boolean;
}

const AppConfigContext = createContext<AppConfigState>({
  config: DEFAULT_CONFIG,
  loading: true,
  error: null,
  notFound: false,
});

/** Fetches GET /finder/config at boot; consumers read via useAppConfig(). */
export const AppConfigProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<AppConfigState>({
    config: DEFAULT_CONFIG,
    loading: true,
    error: null,
    notFound: false,
  });

  const loadConfig = async (isCancelled: () => boolean) => {
    try {
      const config = await fetchFinderConfig();
      if (isCancelled()) return;
      setPublished(config.isPublished);
      setState({
        config, loading: false, error: null, notFound: false,
      });
    } catch (err) {
      if (isCancelled()) return;
      const notFound = axios.isAxiosError(err) && err.response?.status === 404;

      // Preview bounce (loop-safe): 404 + preview link → Premium login, once.
      // If we already bounced (preview_auth set) but still 404, fall through to
      // NotFound so we never bounce twice.
      if (notFound && isPreviewIntent() && !hasPreviewAuthAttempt()) {
        const loginUrl = resolvePreviewLoginUrl();
        if (loginUrl) {
          window.location.assign(loginUrl);
          return;
        }
      }

      const error = err instanceof Error ? err : new Error('Request failed');
      setState({
        config: DEFAULT_CONFIG, loading: false, error, notFound,
      });
    }
  };

  useEffect(() => {
    let cancelled = false;
    loadConfig(() => cancelled);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <AppConfigContext.Provider value={state}>{children}</AppConfigContext.Provider>;
};

export const useAppConfig = (): AppConfigState => useContext(AppConfigContext);
